'use client';

import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import IconButtonGray from '@/components/formElements/iconButtons/IconButtonGray';
import { color, shadowSoft, spacing } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useEntraAuth } from './hooks/useEntraAuth';
import IconChevronDown from '@/assets/icons/IconChevronDown';
import Link from 'next/link';
import IconClose from '@/assets/icons/IconClose';
import { useOptionalCustomerDataContext } from './contexts/CustomerDataContext';
import { hasCustomerEnforcements } from '@/utils/enforcementHelpers';
import { useAccountsContext } from './contexts/AccountsContext';
import { getSwitchAccountPath } from '@/utils/switchAccount';
import { useSecondaryAccountRestrictions } from './hooks/useSecondaryAccountRestrictions';

const getMenuBackground = (level?: number) => {
    if (level === 1) return color.gray[10];
    if (level === 2) return color.gray[20];
    return color.gray[5];
};

interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

const MenuWrapper = styled.div<{ isOpen: boolean }>`
    position: absolute;
    top: 104px;
    right: 0;
    background-color: ${color.gray[5]};
    box-shadow: ${shadowSoft[4]};
    width: 332px;
    z-index: 30;
    display: ${props => (props.isOpen ? 'block' : 'none')};
    padding: ${spacing[16]} 0;

    @media (max-width: 540px) {
        top: 94px;
        width: 100%;
    }
`;

const MenuButton = styled.button<{ level?: number }>`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: none;
    border-radius: 0;
    height: 46px;
    padding: 0 ${spacing[16]};
    background: ${({ level }) => getMenuBackground(level)};
    color: ${color.gray[80]};
    cursor: pointer;
    text-align: left;
    overflow: hidden;
    white-space: nowrap;

    &:focus-visible {
        outline: 2px solid ${color.brand[50]};
        outline-offset: -2px;
    }

    @media (max-width: 540px) {
        padding: 0 ${spacing[24]};
    }
`;

const MenuLink = styled(Link)<{ level?: number }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 46px;
    padding: 0 ${spacing[16]};
    background: ${({ level }) => getMenuBackground(level)};
    color: ${color.gray[80]};
    text-decoration: none;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &:focus-visible {
        outline: 2px solid ${color.brand[50]};
        outline-offset: -2px;
    }

    @media (max-width: 540px) {
        padding: 0 ${spacing[24]};
    }
`;

const NavList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.2s ease-in-out;
    flex-shrink: 0;
`;

const MenuButtonText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

const MenuLinkText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

const SubMenuContainer = styled.ul<{ isOpen: boolean }>`
    overflow: hidden;
    max-height: ${props => (props.isOpen ? '500px' : '0')};
    transition: max-height 0.3s ease-in-out;
    list-style: none;
    margin: 0;
    padding: 0;
`;

// Splitting this component is a structural refactor with its own ticket, not a defect fix.
// react-doctor-disable-next-line no-giant-component
export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
    const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
    const tMain = useTranslations('MainNavigation');
    const tCustomer = useTranslations('CustomerNavigation');
    const tHeader = useTranslations('Header');
    const pathname = usePathname();
    const router = useRouter();
    const { isAuthenticated, logout } = useEntraAuth();
    const { accounts, selectAccount } = useAccountsContext();
    const locale = pathname.split('/')[1];
    const prevPathnameRef = useRef(pathname);

    const {
        canViewFinancials,
        canViewContainerInfo,
        canViewTowingCharges,
        canViewCopartTitleCheck,
    } = useSecondaryAccountRestrictions();

    // Rendered both inside and outside CustomerDataProvider, so read the context optionally.
    const customerDataContext = useOptionalCustomerDataContext();
    const customerData = customerDataContext?.customerData ?? null;
    const isDockReceiptShown = customerDataContext?.isDockReceiptShown ?? false;
    const isClientArchived = customerDataContext?.isClientArchived ?? false;
    const hasEnforcements = hasCustomerEnforcements(customerData);

    const toggleSubmenu = (key: string) => {
        setOpenSubmenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleLogout = async () => {
        onClose();
        await logout();
    };

    const handleSwitchAccount = () => {
        onClose();
        void selectAccount(null);
        router.push(getSwitchAccountPath(locale, pathname));
    };

    // Deliberate reset of local state when the open prop flips; the guard keeps it one-shot.
    // react-doctor-disable-next-line no-reset-all-state-on-prop-change
    useEffect(() => {
        if (!isOpen) {
            // react-doctor-disable-next-line no-adjust-state-on-prop-change
            setOpenSubmenus(new Set());
        }
    }, [isOpen]);

    useEffect(() => {
        if (prevPathnameRef.current !== pathname && isOpen) {
            onClose();
        }
        prevPathnameRef.current = pathname;
    }, [pathname, isOpen, onClose]);

    return (
        <MenuWrapper isOpen={isOpen}>
            <IconButtonGray
                sx={{ position: 'absolute', top: '16px', right: '4px', width: '46px', height: '46px' }}
                onClick={onClose}
            >
                <IconClose color={color.gray[70]} />
            </IconButtonGray>
            <NavList>
                <li>
                    <MenuLink href={`/${locale}/cargo`} onClick={onClose} style={{ marginRight: '50px' }}>
                        <MenuLinkText>
                            <Typography variant="label6medium">{tMain('cargo')}</Typography>
                        </MenuLinkText>
                    </MenuLink>
                </li>

                <li>
                    <MenuLink href={`/${locale}/company/about-us`} onClick={onClose}>
                        <MenuLinkText>
                            <Typography variant="label6medium">{tMain('company')}</Typography>
                        </MenuLinkText>
                    </MenuLink>
                </li>

                <li>
                    <MenuLink href={`/${locale}/services/general-info`} onClick={onClose}>
                        <MenuLinkText>
                            <Typography variant="label6medium">{tMain('services')}</Typography>
                        </MenuLinkText>
                    </MenuLink>
                </li>

                <li>
                    <MenuLink href={`/${locale}/contacts`} onClick={onClose}>
                        <MenuLinkText>
                            <Typography variant="label6medium">{tMain('contacts')}</Typography>
                        </MenuLinkText>
                    </MenuLink>
                </li>

                <li>
                    <MenuLink href={`/${locale}/gg-autosales/about-us`} onClick={onClose}>
                        <MenuLinkText>
                            <Typography variant="label6medium">{tMain('autosales')}</Typography>
                        </MenuLinkText>
                    </MenuLink>
                </li>

                {isAuthenticated && (
                    <>
                        {!hasEnforcements && (
                            <li>
                                <MenuButton
                                    onClick={() => toggleSubmenu('customerZone')}
                                    aria-expanded={openSubmenus.has('customerZone')}
                                >
                                    <MenuButtonText>
                                        <Typography variant="label6medium">{tMain('customerZone')}</Typography>
                                    </MenuButtonText>
                                    <ChevronIcon isOpen={openSubmenus.has('customerZone')}>
                                        <IconChevronDown color={color.gray[70]} />
                                    </ChevronIcon>
                                </MenuButton>

                                <SubMenuContainer isOpen={openSubmenus.has('customerZone')}>
                                    <li>
                                        <MenuLink href={`/${locale}/customer/vehicles`} level={1} onClick={onClose}>
                                            <MenuLinkText>
                                                <Typography variant="label6medium">{tCustomer('vehicles')}</Typography>
                                            </MenuLinkText>
                                        </MenuLink>
                                    </li>

                                    {isDockReceiptShown && canViewContainerInfo && (
                                        <li>
                                            <MenuLink
                                                href={`/${locale}/customer/containers`}
                                                level={1}
                                                onClick={onClose}
                                            >
                                                <MenuLinkText>
                                                    <Typography variant="label6medium">
                                                        {tCustomer('containers')}
                                                    </Typography>
                                                </MenuLinkText>
                                            </MenuLink>
                                        </li>
                                    )}

                                    {canViewCopartTitleCheck ? (
                                        <li>
                                            <MenuButton
                                                level={1}
                                                onClick={() => toggleSubmenu('titleCheck')}
                                                aria-expanded={openSubmenus.has('titleCheck')}
                                            >
                                                <MenuButtonText>
                                                    <Typography variant="label6medium">
                                                        {tCustomer('titleCheck')}
                                                    </Typography>
                                                </MenuButtonText>
                                                <ChevronIcon isOpen={openSubmenus.has('titleCheck')}>
                                                    <IconChevronDown color={color.gray[70]} />
                                                </ChevronIcon>
                                            </MenuButton>

                                            <SubMenuContainer isOpen={openSubmenus.has('titleCheck')}>
                                                <li>
                                                    <MenuLink
                                                        href={`/${locale}/customer/check/info`}
                                                        level={2}
                                                        onClick={onClose}
                                                    >
                                                        <MenuLinkText>
                                                            <Typography variant="label6medium">
                                                                {tCustomer('titleCheckInfo')}
                                                            </Typography>
                                                        </MenuLinkText>
                                                    </MenuLink>
                                                </li>

                                                <li>
                                                    <MenuLink
                                                        href={`/${locale}/customer/check/copart`}
                                                        level={2}
                                                        onClick={onClose}
                                                    >
                                                        <MenuLinkText>
                                                            <Typography variant="label6medium">
                                                                {tCustomer('copartTitleCheck')}
                                                            </Typography>
                                                        </MenuLinkText>
                                                    </MenuLink>
                                                </li>
                                            </SubMenuContainer>
                                        </li>
                                    ) : (
                                        <li>
                                            <MenuLink
                                                href={`/${locale}/customer/check/info`}
                                                level={1}
                                                onClick={onClose}
                                            >
                                                <MenuLinkText>
                                                    <Typography variant="label6medium">
                                                        {tCustomer('titleCheck')}
                                                    </Typography>
                                                </MenuLinkText>
                                            </MenuLink>
                                        </li>
                                    )}

                                    {canViewTowingCharges && (
                                        <li>
                                            <MenuLink href={`/${locale}/customer/towing`} level={1} onClick={onClose}>
                                                <MenuLinkText>
                                                    <Typography variant="label6medium">
                                                        {tCustomer('towingCharges')}
                                                    </Typography>
                                                </MenuLinkText>
                                            </MenuLink>
                                        </li>
                                    )}

                                    {canViewFinancials && (
                                        <li>
                                            <MenuButton
                                                level={1}
                                                onClick={() => toggleSubmenu('accounting')}
                                                aria-expanded={openSubmenus.has('accounting')}
                                            >
                                            <MenuButtonText>
                                                <Typography variant="label6medium">
                                                    {tCustomer('accounting')}
                                                </Typography>
                                            </MenuButtonText>
                                            <ChevronIcon isOpen={openSubmenus.has('accounting')}>
                                                <IconChevronDown color={color.gray[70]} />
                                            </ChevronIcon>
                                        </MenuButton>

                                        <SubMenuContainer isOpen={openSubmenus.has('accounting')}>
                                            <li>
                                                <MenuLink
                                                    href={`/${locale}/customer/accounting/invoices`}
                                                    level={2}
                                                    onClick={onClose}
                                                >
                                                    <MenuLinkText>
                                                        <Typography variant="label6medium">
                                                            {tCustomer('invoices')}
                                                        </Typography>
                                                    </MenuLinkText>
                                                </MenuLink>
                                            </li>

                                            <li>
                                                <MenuLink
                                                    href={`/${locale}/customer/accounting/payments`}
                                                    level={2}
                                                    onClick={onClose}
                                                >
                                                    <MenuLinkText>
                                                        <Typography variant="label6medium">
                                                            {tCustomer('payments')}
                                                        </Typography>
                                                    </MenuLinkText>
                                                </MenuLink>
                                            </li>

                                            <li>
                                                <MenuLink
                                                    href={`/${locale}/customer/accounting/balance`}
                                                    level={2}
                                                    onClick={onClose}
                                                >
                                                    <MenuLinkText>
                                                        <Typography variant="label6medium">
                                                            {tCustomer('balance')}
                                                        </Typography>
                                                    </MenuLinkText>
                                                </MenuLink>
                                            </li>
                                        </SubMenuContainer>
                                        </li>
                                    )}
                                </SubMenuContainer>
                            </li>
                        )}

                        {hasEnforcements && (
                            <li>
                                <MenuLink href={`/${locale}/customer`} onClick={onClose}>
                                    <MenuLinkText>
                                        <Typography variant="label6medium">{tMain('customerZone')}</Typography>
                                    </MenuLinkText>
                                </MenuLink>
                            </li>
                        )}

                        {!isClientArchived && (
                            <li>
                                <MenuLink href={`/${locale}/customer/account`} onClick={onClose}>
                                    <MenuLinkText>
                                        <Typography variant="label6medium">{tHeader('accountSettings')}</Typography>
                                    </MenuLinkText>
                                </MenuLink>
                            </li>
                        )}

                        {accounts.length > 1 && (
                            <li>
                                <MenuButton onClick={handleSwitchAccount}>
                                    <MenuButtonText>
                                        <Typography variant="label6medium">{tHeader('switchAccount')}</Typography>
                                    </MenuButtonText>
                                </MenuButton>
                            </li>
                        )}

                        <li>
                            <MenuButton onClick={handleLogout}>
                                <MenuButtonText>
                                    <Typography variant="label6medium">{tHeader('logout')}</Typography>
                                </MenuButtonText>
                            </MenuButton>
                        </li>
                    </>
                )}
            </NavList>
        </MenuWrapper>
    );
}
